import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, TrendingDown, AlertCircle, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const colourStyles = {
  green: { bg: 'bg-emerald-50 border-emerald-200', pill: 'bg-emerald-500 text-white', dot: 'bg-emerald-500', text: 'text-emerald-700' },
  yellow: { bg: 'bg-amber-50 border-amber-200', pill: 'bg-amber-500 text-white', dot: 'bg-amber-500', text: 'text-amber-700' },
  red: { bg: 'bg-red-50 border-red-200', pill: 'bg-red-500 text-white', dot: 'bg-red-500', text: 'text-red-700' },
};

const RevisionHeatmap = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/teacher/revision-heatmap`);
        setData(res.data);
      } catch (e) {
        console.error('heatmap load error', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 flex items-center justify-center" data-testid="heatmap-loading">
        <Loader2 className="animate-spin text-purple-500" size={22} />
      </div>
    );
  }

  const chapters = data?.chapters || [];
  const summary = data?.summary || { total: 0, ready: 0, needs_revision: 0, not_ready: 0 };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="revision-heatmap">
      <div className="px-5 py-4 bg-gradient-to-r from-slate-800 to-slate-900 text-white flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Revision Heatmap</h3>
          <p className="text-slate-300 text-xs">Chapter-level class readiness — traffic light on latest scores</p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" /> Ready {summary.ready}</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> Revise {summary.needs_revision}</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-400" /> Not Ready {summary.not_ready}</span>
        </div>
      </div>

      {chapters.length === 0 ? (
        <div className="p-10 text-center text-gray-400 text-sm">
          No topic-test data yet. Once students start attempting Topic Tests, the heatmap fills up automatically.
        </div>
      ) : (
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {chapters.map((c, i) => {
            const s = colourStyles[c.colour] || colourStyles.yellow;
            const Icon = c.colour === 'green' ? CheckCircle2 : c.colour === 'red' ? AlertCircle : TrendingDown;
            return (
              <div
                key={`${c.class_level}-${c.subject}-${c.chapter}-${i}`}
                className={`rounded-lg border p-3 ${s.bg}`}
                data-testid={`heatmap-cell-${i}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wide text-gray-500">
                      Class {c.class_level} · {c.subject}
                    </p>
                    <p className="font-semibold text-gray-800 truncate" title={c.chapter}>{c.chapter}</p>
                  </div>
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${s.pill}`}>
                    {c.label}
                  </span>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <p className={`text-2xl font-bold ${s.text}`}>{c.readiness}%</p>
                    <p className="text-[11px] text-gray-500">{c.students_attempted} student{c.students_attempted !== 1 ? 's' : ''}</p>
                  </div>
                  <Icon className={s.text} size={22} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RevisionHeatmap;
